import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { UserProfile } from './UserProfile';
import { Account } from './UserProfile';

import {
  createUserProfile,
  deleteUserProfile,
  findOrCreateUserProfile,
  updateUserProfile,
} from './UserProfileConstants';

/**
 * React Query Hook für die Verwaltung von Benutzerprofilen
 * Bietet Funktionen zum Laden, Erstellen, Aktualisieren und Löschen von Benutzerprofilen
 */
export const useUserProfile = (account?: Account) => {
  const queryClient = useQueryClient();

  // Query zum Laden des Benutzerprofils
  const {
    data: userProfile,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userProfile', account?.email],
    queryFn: () => findOrCreateUserProfile(account as Account),
    enabled: !!account?.email,
    staleTime: 5 * 60 * 1000, // 5 Minuten
    gcTime: 30 * 60 * 1000, // 30 Minuten
  });

  // Mutation zum Erstellen eines Benutzerprofils
  const createMutation = useMutation({
    mutationFn: createUserProfile,
    onSuccess: (data: UserProfile) => {
      queryClient.setQueryData(['userProfile', data.email], data);
    },
  });

  // Mutation zum Aktualisieren eines Benutzerprofils
  const updateMutation = useMutation({
    mutationFn: updateUserProfile,
    onSuccess: (data: UserProfile) => {
      queryClient.setQueryData(['userProfile', data.email], data);
    },
  });

  // Mutation zum Löschen eines Benutzerprofils
  const deleteMutation = useMutation({
    mutationFn: deleteUserProfile,
    onSuccess: () => {
      if (account?.email) {
        queryClient.removeQueries({ queryKey: ['userProfile', account.email] });
      }
    },
  });

  return {
    userProfile,
    isLoading,
    error,
    refetch,
    createUserProfile: createMutation.mutate,
    updateUserProfile: updateMutation.mutate,
    deleteUserProfile: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
  };
};
